import React, { useEffect, useState, useCallback, useMemo } from "react";
import { fetchWrongQuestions } from "../../../utils/growth"; // same utility RecentMistakesWidget uses
import { Button, Form, Pagination } from "react-bootstrap";
import { ArrowLeft, CheckCircleFill, ArrowClockwise } from "react-bootstrap-icons";
import {
  INK,
  MUTED,
  CARD,
  HAIRLINE,
  BRAND,
  BRAND_SOFT,
  CORRECT,
  BODY_FONT,
  EXAM_TYPE_META,
  SkeletonRow,
  WrongQuestionRow,
} from "./Wrongquestionsshared"; // adjust path to wherever wrongQuestionsShared.js lives
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {getUserInformation, getUserPermission, getUserRole} from "../../../store/userRole";
import {getSingalClassroomData} from "../../../utils/classrooms";
import {setNavbarParams, setText} from "../../../store/navbar";
import {fetchSubscription} from "../../../store/subscriptionSlice";
import {getStudentEndPoints, MustIncludedSidebarItems} from "../../../constants/sidebar";
import {setLinks, showLinks} from "../../../store/sidebar";
import {extractModulesInMentionedOrder} from "../../../utils/validations";
import Loader from "../../Loader";

const PAGE_BG = "#F4F5F8";

// Filter pills for the exam-type toggle bar — built from the same
// EXAM_TYPE_META the rows themselves use, so labels always stay in sync.
const EXAM_TYPE_FILTERS = [
  { value: "", label: "All Types" },
  ...Object.entries(EXAM_TYPE_META).map(([value, meta]) => ({ value, label: meta.label })),
];

/* Windowed pagination numbers with ellipses for larger page counts. */
function getPageWindow(current, total, span = 1) {
  const pages = new Set([1, total, current]);
  for (let i = 1; i <= span; i++) {
    if (current - i >= 1) pages.add(current - i);
    if (current + i <= total) pages.add(current + i);
  }
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const withGaps = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) withGaps.push("ellipsis-" + p);
    withGaps.push(p);
  });
  return withGaps;
}

/**
 * "View All" wrong-questions page: filterable by subject / exam type,
 * paginated, with a back button. Rows reuse the exact same
 * WrongQuestionRow component as the dashboard widget (via
 * wrongQuestionsShared), so question/option rendering never drifts
 * between the two screens.
 */
export default function WrongQuestionsPage({
  isMobileView = false,
}) {
  const [page, setPage] = useState(1);
  const [title] = useState('Wrong Questions')
  const roleOfTheUser = useSelector(getUserRole);
  const [subjectOptions, setSubjectOptions] = useState([{label: 'All', value: ''}])
  const rolePermissions = useSelector(getUserPermission);
  const navigate = useNavigate();
  const [subjectId, setSubjectId] = useState("");
  const [examType, setExamType] = useState("");
  const [state, setState] = useState({ status: "loading", items: [], totalPages: 1, total: 0 });
  const [openRows, setOpenRows] = useState(() => new Set());
  const [pageSize] = useState(12)
  const { id: classroomId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const studentQueryId = queryParams.get("studentId")
  const userInformation = useSelector(getUserInformation);
  const studentId = userInformation?._id;
  const [thumbnailLoader, setThumbnailLoader] = useState(true);
  const [hasIeltsExams, setHasIeltsExams] = useState(false)
  const [classroomName, setClassroomName] = useState('');
  const dispatch = useDispatch();
  const [modules, setModules] = useState([]);

  const subjectMapObj = useMemo(()=>{
    const resultObj = {}
    for(let subject of subjectOptions) {
        if (subject.label !== 'All')
            resultObj[subject.value] = subject.label
    }
    return resultObj
  },[subjectOptions])


    const getClassroomDetails = async () => {
      try {
        setThumbnailLoader(true);
        const response = await getSingalClassroomData(classroomId);
        if (response?.data?.classRoom) {
          if (response?.data?.classRoom?.name) {
            setClassroomName(response?.data?.classRoom?.name);
            setModules(response?.data?.classRoom?.modulesIncluded);
            setHasIeltsExams(response?.data?.classRoom?.hasIeltsExams)
            const subjectOpts = response?.data?.classRoom?.course?.subjects?.map(subject => ({label: subject?.name, value: subject?._id})) ?? []
            setSubjectOptions([{label: 'All', value: ''},...subjectOpts])
          }
        }
        setThumbnailLoader(false);
      } catch (error) {
        console.error("Error fetching classrooms:", error);
        setThumbnailLoader(false);
        // Handle error, show a message to the user, etc.
      }
    };
    useEffect(() => {
      getClassroomDetails();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    useEffect(() => {
        if(roleOfTheUser === 'Student') {
            const heading = "student-dashboard";
            let userId = userInformation?._id;
            dispatch(setText(heading));
            dispatch(setNavbarParams(`My Classrooms | ${classroomName}`));
            dispatch(fetchSubscription({ classRoomId: classroomId, userId }));
            const defaultItems = [
                ...getStudentEndPoints(
                classroomId,
                roleOfTheUser,
                false,
                rolePermissions,
                ),
            ];
            const sidebarItems = defaultItems.filter(
                (item) =>
                modules.includes(item?.value) ||
                MustIncludedSidebarItems.includes(item?.value),
            );
            const indOfItem = sidebarItems?.findIndex(
                (item) => item.value === "Dashboard",
            );
            if (indOfItem !== -1) {
                sidebarItems[indOfItem].selected = true;
            }
            dispatch(setLinks(extractModulesInMentionedOrder(sidebarItems, modules)));
            dispatch(showLinks());
        }
    }, [
      classroomId,
      classroomName,
      dispatch,
      modules,
      roleOfTheUser,
      rolePermissions,
      userInformation?._id,
    ]);


  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const res = await fetchWrongQuestions(classroomId, roleOfTheUser === 'Student' ? studentId : studentQueryId, {
        page,
        limit: pageSize,
        subjectId: subjectId || undefined,
        examType: examType || undefined,
        hasIeltsExams
      });
      setState({
        status: "ready",
        items: res?.wrongAnswers || [],
        totalPages: res?.pagination?.totalPages || 1,
        total: res?.pagination?.total ?? (res?.wrongAnswers || []).length,
      });
    } catch (err) {
      setState({ status: "error", items: [], totalPages: 1, total: 0 });
    }
  }, [classroomId,studentQueryId, studentId, roleOfTheUser, page, pageSize, subjectId, examType, hasIeltsExams]);

  useEffect(() => {
    if(!thumbnailLoader) {
      load();
      setOpenRows(new Set()); // collapse any open rows when the underlying page/filters change
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, thumbnailLoader]);

  const handleSubjectChange = (e) => {
    setSubjectId(e.target.value);
    setPage(1); // reset to first page on filter change
  };

  const handleExamTypeChange = (value) => {
    setExamType(value);
    setPage(1); // reset to first page on filter change
  };

  const handleBack = () => {
    navigate(-1)
  };

  const toggleRow = (key) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const pageNumbers = useMemo(() => getPageWindow(page, state.totalPages), [page, state.totalPages]);

    if (thumbnailLoader) {
      return <Loader />;
    }

  return (
    <div style={{ minHeight: "100%", background: PAGE_BG, fontFamily: BODY_FONT }}>
      <style>{`
        @keyframes wqpPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
        .rmw-pulse { animation: wqpPulse 1.3s ease-in-out infinite; }
      `}</style>

      <div style={{ margin: "0 auto", padding: "24px 20px 48px" }}>
        {/* Back button + title */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <button
            type="button"
            onClick={handleBack}
            aria-label="Go back"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 10,
              border: `1px solid ${HAIRLINE}`,
              background: CARD,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={16} color={INK} />
          </button>
          <div>
            <div style={{ fontSize: 19, fontWeight: 700, color: INK }}>{title}</div>
            <div style={{ fontSize: 12.5, color: MUTED, marginTop: 1 }}>
              {state.status === "ready" ? `${state.total} question${state.total === 1 ? "" : "s"} to review` : "\u00A0"}
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <Form.Select
            value={subjectId}
            onChange={handleSubjectChange}
            style={{
              width: "auto",
              minWidth: 170,
              fontSize: 13.5,
              borderColor: HAIRLINE,
              borderRadius: 10,
              padding: "8px 32px 8px 12px",
            }}
          >
            {subjectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {EXAM_TYPE_FILTERS.map((f) => {
              const active = examType === f.value;
              return (
                <button
                  key={f.value || "all"}
                  type="button"
                  onClick={() => handleExamTypeChange(f.value)}
                  style={{
                    padding: "7px 14px",
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 600,
                    border: `1px solid ${active ? BRAND : HAIRLINE}`,
                    background: active ? BRAND : CARD,
                    color: active ? "#fff" : INK,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* List card */}
        <div style={{ background: CARD, border: `1px solid ${HAIRLINE}`, borderRadius: 16, padding: "6px 20px", boxSizing: "border-box" }}>
          {state.status === "loading" && (
            <div>
              {Array.from({ length: Math.min(pageSize, 6) }).map((_, i) => (
                <React.Fragment key={i}>
                  <SkeletonRow />
                  {i < Math.min(pageSize, 6) - 1 && <div style={{ borderTop: `1px solid ${HAIRLINE}` }} />}
                </React.Fragment>
              ))}
            </div>
          )}

          {state.status === "error" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "40px 12px",
                color: MUTED,
                fontSize: 13.5,
              }}
            >
              <span>Couldn't load these questions right now.</span>
              <Button
                onClick={load}
                size="sm"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: BRAND_SOFT,
                  color: BRAND,
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <ArrowClockwise size={13} /> Try again
              </Button>
            </div>
          )}

          {state.status === "ready" && state.items.length === 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "40px 12px",
                textAlign: "center",
              }}
            >
              <CheckCircleFill size={26} color={CORRECT} />
              <div style={{ fontSize: 14.5, color: INK, fontWeight: 600 }}>No wrong questions found</div>
              <div style={{ fontSize: 12.5, color: MUTED }}>Try a different subject or exam type filter.</div>
            </div>
          )}

          {state.status === "ready" &&
            state.items.map((item, i) => {
              const key = item?.attemptId + "-" + (item?.question?._id || i);
              return (
                <React.Fragment key={key}>
                  <WrongQuestionRow item={item} isOpen={openRows.has(key)} onToggle={() => toggleRow(key)} subjectMap={subjectMapObj} isMobileView={isMobileView} hasIeltsExams={hasIeltsExams} />
                  {i < state.items.length - 1 && <div style={{ borderTop: `1px solid ${HAIRLINE}` }} />}
                </React.Fragment>
              );
            })}
        </div>

        {/* Pagination */}
        {state.status === "ready" && state.totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
            <Pagination style={{ marginBottom: 0 }}>
              <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
              {pageNumbers.map((p) =>
                typeof p === "string" ? (
                  <Pagination.Ellipsis key={p} disabled />
                ) : (
                  <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                )
              )}
              <Pagination.Next disabled={page === state.totalPages} onClick={() => setPage((p) => Math.min(state.totalPages, p + 1))} />
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
}